<template>
  <field-label :label="label" :required="required" :hideRequired="hideRequired" v-bind="$attrs">
    <v-autocomplete
      :class="['search-field', customClass]"
      :label="autocomplete.label"
      :dense="autocomplete.dense"
      :outlined="autocomplete.outlined"
      :solo="autocomplete.solo"
      :filled="autocomplete.filled"
      clearable
      :small-chips="multiple"
      deletable-chips
      :cache-items="cacheItems"
      return-object
      :items="computedItems"
      :item-text="itemText"
      :item-value="itemValue"
      :rules="computedRules"
      :value="value"
      :loading="loading"
      :no-data-text="computedNoDataText"
      :placeholder="computedPlaceholder"
      :multiple="multiple"
      :filter="defaultFilter"
      :hide-details="hideDetails"
      :hint="hint"
      :persistent-hint="persistentHint"
      :disabled="disabled"
      @update:search-input="fetchItemsBySearchTerm"
      @input="$emit('input', $event)"
      :height="height"
    >
      <template #append-outer v-if="this.fetchAll && this.fetchError">
        <v-btn class="mt-n2" icon :loading="loading" @click="fetchAllItems" color="primary"
          ><v-icon>mdi-refresh</v-icon></v-btn
        >
      </template>
      <template #item="{ item, on, attrs }">
        <v-list-item v-on="on" v-bind="attrs">
          <v-list-item-action v-if="multiple">
            <v-simple-checkbox :value="attrs.inputValue" v-ripple v-on="on" />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>
              <span v-html="getItemTextWithHighlight(item)"></span>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
  </field-label>
</template>

<script>
import FieldLabel from './FieldLabel.vue'

export default {
  name: 'SearchServerField',
  components: { FieldLabel },
  props: {
    value: undefined,
    label: String,
    required: Boolean,
    placeholder: String,
    multiple: Boolean,
    rules: Array,
    itemText: {
      type: [String, Function],
      default: 'name',
    },
    cacheItems: {
      type: Boolean,
      default: true,
    },
    itemValue: {
      type: [String, Function],
      default: 'id',
    },
    customFilter: {
      type: Function,
    },
    resultsLimit: {
      type: Number,
      default: 10,
    },
    minSearchLength: {
      type: Number,
      default: 3,
    },
    path: {
      type: String,
      required: true,
    },
    hint: String,
    persistentHint: Boolean,
    hideDetails: Boolean,
    disabled: Boolean,
    /**
     * Traz TODOS os registros do servidor assim que o componente é montado.
     * Em caso de erro, exibe um botão para permitir ao usuário tentar buscar os
     * dados novamente.
     */
    fetchAll: Boolean,
    customClass: {} || String,
    /**
     * Ação de busca customizada.
     * Se for uma string, é utilizada no lugar de 'path;
     * se for uma função, é a função utilizada no lugar a ação padrão
     */
    customFetch: [String, Function],
    autocomplete: {
      type: Object,
      default(rawProps) {
        return {
          dense: true,
          outlined: true,
          filled: false,
          solo: false,
        }
      },
    },
    hideRequired: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    computedMinCharsToSearchText() {
      if (this.fetchAll) {
        return this.fetchError ? 'Erro ao buscar dados. Clique no botão para tentar novamente' : ''
      }

      return this.minSearchLength === 1
        ? 'Digite ao menos um caractere para buscar'
        : `Digite ao menos ${this.minSearchLength} caracteres para buscar`
    },
    computedNoDataText() {
      if (!this.search || this.search.length < this.minSearchLength) {
        return this.computedMinCharsToSearchText || 'Nenhum resultado encontrado'
      }

      return 'Nenhum resultado encontrado'
    },
    computedPlaceholder() {
      return this.placeholder ?? this.computedMinCharsToSearchText
    },
    computedRules() {
      const rules = []
      if (this.required) {
        rules.push(this.$validation.required())
      }
      if (this.rules) {
        rules.push(...this.rules)
      }
      return rules
    },
    computedItems() {
      if (this.items.length === 0) {
        if (Array.isArray(this.value)) {
          return this.$util.copy(this.value)
        }
        if (!!this.value) {
          return [this.$util.copy(this.value)]
        }
      }
      if (this.customFilter) {
        return this.items.filter(this.customFilter)
      }

      return this.items
    },
  },
  data() {
    return {
      search: '',
      loading: false,
      items: [],
      fetchError: false,
    }
  },
  methods: {
    defaultFilter(item, queryText, itemText) {
      return this.$util.hasTermInAnyString(queryText, itemText)
    },
    separateTextBySearchMatchRegion(text) {
      if (!text) {
        text = ''
      }

      const searchInput = this.$util.toComparableStringLowerCase(this.search)
      const index = this.$util.toComparableStringLowerCase(text).indexOf(searchInput)

      if (index < 0) {
        return { start: text, middle: '', end: '' }
      }

      const start = text.slice(0, index)
      const middle = text.slice(index, index + searchInput.length)
      const end = text.slice(index + searchInput.length)
      return { start, middle, end }
    },
    genHighlight(text) {
      return `<span class="v-list-item__mask">${this.$util.escapeHTML(text)}</span>`
    },
    getItemTextWithHighlight(item) {
      let text = ''

      if (!item) {
        return text
      }

      if (typeof this.itemText === 'function') {
        text = this.itemText(item)
      } else {
        text = this.$util.deepObjectValue(item, this.itemText)
      }

      const { start, middle, end } = this.separateTextBySearchMatchRegion(text)

      return `${this.$util.escapeHTML(start)}${this.genHighlight(middle)}${this.$util.escapeHTML(end)}`
    },
    async fetchItemsBySearchTerm(search) {
      this.search = search || ''

      if (this.fetchAll) {
        return
      }

      if (this.loading || this.search.length < this.minSearchLength) {
        return
      }

      await this.fetchItems(this.$util.clearString(this.search), this.resultsLimit)
    },
    async fetchAllItems() {
      this.fetchItems(null, 0)
    },
    async fetchItems(search, limit) {
      try {
        this.fetchError = false
        this.loading = true

        let functionToCall = this.$api.fetchOptions
        let path = this.path
        if (this.customFetch) {
          if (typeof this.customFetch === 'function') {
            functionToCall = this.customFetch
          } else {
            path = this.customFetch
          }
        }
        this.items = await functionToCall(path, search, limit)
      } catch (error) {
        this.fetchError = true
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    if (this.fetchAll) {
      this.fetchAllItems()
    }
  },
}
</script>

<style scoped>
.v-autocomplete:not(.v-input--is-focused).v-select--chips :deep(input) {
  max-height: inherit;
}
:deep(.v-select__slot) {
  padding: 4px 0;
}
:deep(.v-select__selections) {
  gap: 4px;
}
</style>
